// frontend/src/pages/Suppliers/EditSuppliers.jsx
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AlertCircle,
  CreditCard,
  Loader2,
  Mail,
  Phone,
  Save,
  User,
  UserRoundCog,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Suppliers.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ ROBUST: ALWAYS returns a 3‑letter currency code as a string
const getCompanyCurrency = (company) => {
  if (!company) return "USD";

  let currency = null;

  // 1. Try company.currency (could be object or string)
  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  // 2. Try company.currency_code
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  // 3. Try company.base_currency
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  // Final safety: default to USD if not a valid 3-letter code
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

export default function EditSuppliers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const { getCurrencySymbol } = useCurrency();

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [supplier, setSupplier] = useState(null);
  const [formData, setFormData] = useState({
    supplier_name: "",
    phone: "",
    email: "",
    currency_code: companyCurrency,
    credit_limit: "0.00",
    status: "active",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let active = true;

    const loadSupplier = async () => {
      try {
        const response = await axios.get(`${url}/api/supplier/${id}`);

        if (!active) return;
        const item = response.data?.data;
        setSupplier(item);
        setFormData({
          supplier_name: item?.supplier_name || "",
          phone: item?.phone || "",
          email: item?.email || "",
          currency_code: companyCurrency, // force company currency
          credit_limit: String(item?.credit_limit ?? 0),
          status: item?.status || "active",
        });
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load supplier");
        navigate("/ViewSuppliers");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSupplier();
    return () => {
      active = false;
    };
  }, [id, navigate, companyCurrency]);

  const setField = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const name = formData.supplier_name.trim();
    const creditLimit = Number(formData.credit_limit || 0);

    if (name.length < 2 || name.length > 100) {
      nextErrors.supplier_name = "Supplier name must contain 2 to 100 characters";
    }

    if (formData.phone && !/^[\d\s+()\-]{7,30}$/.test(formData.phone.trim())) {
      nextErrors.phone = "Enter a valid phone number";
    }

    if (
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!/^[A-Za-z]{3}$/.test(formData.currency_code)) {
      nextErrors.currency_code = "Use a three-letter currency code";
    }

    if (!Number.isFinite(creditLimit) || creditLimit < 0) {
      nextErrors.credit_limit = "Credit limit must be zero or greater";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.put(
        `${url}/api/supplier/${id}`,
        {
          supplier_name: formData.supplier_name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim().toLowerCase(),
          currency_code: formData.currency_code.trim().toUpperCase(),
          credit_limit: Number(formData.credit_limit || 0),
          status: formData.status,
        },
      );

      toast.success(response.data?.message || "Supplier updated successfully");
      window.setTimeout(() => navigate("/ViewSuppliers"), 500);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update supplier");
      const serverErrors = error.response?.data?.errors;
      if (Array.isArray(serverErrors)) {
        setErrors((current) => ({
          ...current,
          ...Object.fromEntries(serverErrors.map((item) => [item.field, item.message])),
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="supplier-page-shell">
        <div className="supplier-loading-card">
          <Loader2 className="supplier-spin" size={30} />
          <span>Loading supplier…</span>
        </div>
      </main>
    );
  }

  if (!supplier) return null;

  return (
    <main className="supplier-page-shell">
      <section className="supplier-form-card">
        <button
          type="button"
          className="supplier-close-button"
          onClick={() => navigate("/ViewSuppliers")}
          aria-label="Close supplier form"
        >
          <X size={20} />
        </button>

        <header className="supplier-form-header">
          <div className="supplier-form-icon"><UserRoundCog size={24} /></div>
          <div>
            <p className="supplier-eyebrow">Supplier master</p>
            <h1>Edit supplier</h1>
            <p>Update contact, credit-control and status information.</p>
          </div>
        </header>

        <form className="supplier-form" onSubmit={handleSubmit} noValidate>
          <div className="supplier-field supplier-field--full">
            <label htmlFor="edit-supplier-name"><User size={15} /> Supplier name *</label>
            <input
              id="edit-supplier-name"
              value={formData.supplier_name}
              onChange={(event) => setField("supplier_name", event.target.value)}
              maxLength={100}
            />
            {errors.supplier_name && <small className="supplier-error">{errors.supplier_name}</small>}
          </div>

          <div className="supplier-field">
            <label htmlFor="edit-supplier-phone"><Phone size={15} /> Phone</label>
            <input
              id="edit-supplier-phone"
              value={formData.phone}
              onChange={(event) => setField("phone", event.target.value)}
              maxLength={30}
            />
            {errors.phone && <small className="supplier-error">{errors.phone}</small>}
          </div>

          <div className="supplier-field">
            <label htmlFor="edit-supplier-email"><Mail size={15} /> Email</label>
            <input
              id="edit-supplier-email"
              type="email"
              value={formData.email}
              onChange={(event) => setField("email", event.target.value)}
              maxLength={120}
            />
            {errors.email && <small className="supplier-error">{errors.email}</small>}
          </div>

          {/* ✅ Currency field – disabled and auto-filled with company currency */}
          <div className="supplier-field">
            <label htmlFor="edit-supplier-currency"><CreditCard size={15} /> Currency</label>
            <input
              id="edit-supplier-currency"
              value={formData.currency_code}
              disabled
              style={{
                background: '#f5f5f5',
                cursor: 'not-allowed',
                opacity: 0.8,
              }}
            />
            <small style={{ color: '#666' }}>
              Currency is set to the company's base currency and cannot be changed.
            </small>
            {errors.currency_code && <small className="supplier-error">{errors.currency_code}</small>}
          </div>

          <div className="supplier-field">
            <label htmlFor="edit-supplier-credit-limit"><CreditCard size={15} /> Credit limit</label>
            <input
              id="edit-supplier-credit-limit"
              type="number"
              min="0"
              step="0.01"
              value={formData.credit_limit}
              onChange={(event) => setField("credit_limit", event.target.value)}
            />
            <small className="supplier-hint">Control only; zero means no limit is configured. No Chart of Accounts entry is created.</small>
            {errors.credit_limit && <small className="supplier-error">{errors.credit_limit}</small>}
          </div>

          <div className="supplier-field supplier-field--full">
            <label htmlFor="edit-supplier-status">Status</label>
            <select
              id="edit-supplier-status"
              value={formData.status}
              onChange={(event) => setField("status", event.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <small className="supplier-hint">
              Inactive suppliers are hidden from new purchase transactions.
            </small>
          </div>

          <div className="supplier-form-actions supplier-field--full">
            <button
              type="button"
              className="supplier-button supplier-button--secondary"
              onClick={() => navigate("/ViewSuppliers")}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="supplier-button supplier-button--primary"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="supplier-spin" size={17} /> : <Save size={17} />}
              {submitting ? "Updating…" : "Update supplier"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}